/**
 * THE WAKING SCREEN — the first thing anybody sees.
 *
 * Three questions and one door, asked in the order they matter: **who are you called**, **what
 * woke up**, and **what did it wake up as**. Nothing else. A new player has no stash, no Marks
 * and no opinion yet, so this screen asks only what the world genuinely needs and then gets out
 * of the way.
 *
 * ## Why this is not the Calling tab
 * `Calling.tsx` is the *briefing* view — passives, cooldowns, skill trees, the honest note that
 * switching is free. That is the right screen for somebody on their ninth raid and the wrong one
 * for somebody who has been awake for four seconds. Here a Calling is a silhouette, a name and a
 * paragraph somebody says out loud. The numbers keep until they mean something.
 *
 * ## Why the body is live now, and used to be a photograph
 * This file used to argue that a live turntable meant standing a second three.js renderer up on
 * the cold-start screen before the player had committed to anything, and that four PNGs were the
 * better trade. That was right for what this screen was: the choice was cosmetic, and the bodies
 * were interchangeable. It is not that any more. Since the pivot the choice is a **race**, it is
 * written to the character row once and never again, and it decides the silhouette the whole
 * game draws you with. Deciding that from somebody else's screenshot is the wrong way round. So
 * the preview is the real crowd renderer with one character in it (`waking-preview.ts`, which
 * answers the old objection point by point) — and when a device cannot be had, the screen simply
 * carries on without it rather than showing anybody an error.
 *
 * ## Failure is not the player's problem
 * The one write is `POST /api/character`, and it is the only thing on this screen that can fail
 * in a way the player must hear about: it names them, and it mints the token they walk into the
 * world holding. So it is awaited, it is retried by pressing the door again, and it says what
 * went wrong. Everything else — the preview, a slow ledger — fails quietly.
 */

import type { VNode } from 'preact';
import { useEffect, useRef, useState } from 'preact/hooks';
import { CALLINGS, PLAYABLE_RACES, Race, type CallingDef } from '@stillsun/shared';
import { CALLING_ICON, Icon, isIconName } from '../icons.js';
import { postCharacter } from '../../net/progress-api.js';
import { progressStore } from '../../net/progress-store.js';
import { session } from '../../net/session.js';
import { WakingPreview } from './waking-preview.js';
import './waking.css';

export interface WakingProps {
  token: string;
  /** Everything chosen, already persisted. The host deploys straight into the tutorial. */
  onWake: (chosen: { name: string; race: number; calling: number }) => void;
}

/**
 * The two playable Callings. Shade and Tinker are hidden, not deleted — they are still in
 * `CALLINGS`, still have skill trees, and are refused by the creation route by range — so this
 * list is the gate rather than a filter over the table.
 */
const PLAYABLE_CALLINGS: readonly number[] = [1, 2];

/**
 * A paragraph per choice, in the Choir's own voices (`ui/lore.ts` sets the rules: somebody says
 * it, one turn, nobody addresses a player). Isolde Vane keeps the Lantern Hall and is the person
 * a newly woken Waker actually meets, so the races are hers. The Callings are the Ledger's,
 * which is the only voice in the game allowed to be flat about what a job costs.
 */
const RACE_COPY: Record<number, { name: string; speaker: string; text: string }> = {
  [Race.Human]: {
    name: 'Human',
    speaker: 'Isolde Vane',
    text: 'Born under the Stillsun, like everyone here who is still warm. Three hundred years of us have been born into the long dusk and not one has seen the sun move. You will tire in the ordinary way and bleed in the ordinary way, and when the Choir sings you will hear a sound and not a summons. That last part is worth more than it reads.',
  },
  [Race.Hollow]: {
    name: 'Hollow',
    speaker: 'Isolde Vane',
    text: 'The Choir sang some of its dead back onto their feet and then lost interest in them. Those are the Hollow — bone that kept walking, still wearing what it was buried in. Lanternfall opened the gate to them because they came to it one at a time instead of in a chorus. Nobody has yet settled whether that was their own idea.',
  },
};

const CALLING_COPY: Record<number, { speaker: string; text: string }> = {
  1: {
    speaker: 'the Ledger',
    text: 'Plate, a tower shield, and a standing order to be the thing that gets hit. A Vanguard’s whole art is making the Choir want them and then surviving being wanted. It is not the admired half of a group and everyone in the group knows exactly whose back they are standing behind. Ask any Warden which of the two they would rather lose.',
  },
  2: {
    speaker: 'the Ledger',
    text: 'A lantern in one hand and the other hand kept free for whoever needs it. Wardens hold the light on people rather than on ground, which is why the Choir silences them first and why they are counted before a group sets out. A Warden paying attention holds a line that should have broken. A Warden who is not is a person carrying a lamp.',
  },
};

/** Callsigns are trimmed and capped the same way the server's `sanitizeName` will cap them. */
const NAME_MAX = 20;

export function Waking(props: WakingProps): VNode {
  const { token } = props;
  const [name, setName] = useState(() => session.name.slice(0, NAME_MAX));
  const [race, setRace] = useState(0);
  const [calling, setCalling] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const trimmed = name.trim();
  const named = trimmed.length > 0;
  const ready = named && race !== 0 && calling !== 0;

  // ------------------------------------------------------------------ the preview

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const previewRef = useRef<WakingPreview | null>(null);
  /**
   * Three states, not two. `loading` is the second or so it takes to get a device and the baked
   * bodies; `off` is a browser that could not give us either. They read completely differently to
   * somebody watching an empty box, so the box says which one it is in.
   */
  const [preview, setPreview] = useState<'loading' | 'live' | 'off'>('loading');

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    let cancelled = false;
    void WakingPreview.create(canvas).then((made) => {
      if (cancelled) {
        // The screen was closed while the device was being acquired: give it straight back.
        made?.dispose();
        return;
      }
      if (made === null) {
        setPreview('off');
        return;
      }
      previewRef.current = made;
      setPreview('live');
    });
    const onResize = (): void => {
      previewRef.current?.resize();
    };
    window.addEventListener('resize', onResize);
    return () => {
      cancelled = true;
      window.removeEventListener('resize', onResize);
      previewRef.current?.dispose();
      previewRef.current = null;
    };
  }, []);

  // The preview draws from the same packed `look` word the world draws every body from, so what
  // is turning here is what walks out of the gate.
  useEffect(() => {
    previewRef.current?.setLook({ race, calling });
  }, [race, calling]);

  // ------------------------------------------------------------------- the door

  const wake = (): void => {
    // The guard is the double-press guard as much as the gate: `onWake` deploys a scene, and
    // firing it twice would deploy two.
    if (busy || !ready) return;
    setBusy(true);
    setError('');

    void (async () => {
      try {
        const made = await postCharacter(token, { name: trimmed, race, calling });
        // The name is baked into the JWT and the room reads it from there — walk in on the old
        // token and Lanternfall greets somebody else.
        session.adoptCharacter(made.token, made.name);
        // Optimistic, the way the Calling tab does it, so anything already listening to the
        // store is right before the next round trip lands.
        progressStore.patch({ calling: made.calling });
        void progressStore.load(made.token, true);
        props.onWake({ name: made.name, race: made.race, calling: made.calling });
      } catch (err) {
        // The one failure on this screen worth a sentence: without a character row there is
        // nobody to deploy. Pressing the door again retries.
        console.warn('[stillsun] waking: character not created', err);
        setError('The ledger did not take that. Try the door again.');
        setBusy(false);
      }
    })();
  };

  const list = PLAYABLE_CALLINGS.map((id) => (CALLINGS as Record<number, CallingDef>)[id]).filter(
    (c): c is CallingDef => c !== undefined,
  );

  const voice = calling !== 0 ? CALLING_COPY[calling] : race !== 0 ? RACE_COPY[race] : undefined;

  return (
    <div class="wk">
      <header class="wk-head">
        <h1 class="wk-title">Who woke?</h1>
        <p class="wk-sub">A name, a body, and what the body is for.</p>
      </header>

      <div class="wk-body">
        <div class="wk-cols">
          <div class="wk-choices">
            <section class="wk-block" aria-labelledby="wk-cap-name">
              <h2 class="wk-cap" id="wk-cap-name">
                The Name
              </h2>
              <input
                class="wk-name"
                type="text"
                value={name}
                maxLength={NAME_MAX}
                spellcheck={false}
                autocomplete="off"
                disabled={busy}
                aria-describedby="wk-gate"
                placeholder="Callsign"
                onInput={(ev) => {
                  setName((ev.currentTarget as HTMLInputElement).value);
                }}
              />
            </section>

            <section class="wk-block" aria-labelledby="wk-cap-race">
              <h2 class="wk-cap" id="wk-cap-race">
                The Body
              </h2>
              <div class="wk-races" role="group" aria-labelledby="wk-cap-race">
                {PLAYABLE_RACES.map((r) => {
                  const copy = RACE_COPY[r];
                  const on = r === race;
                  return (
                    <button
                      key={String(r)}
                      type="button"
                      class={`wk-race${on ? ' on' : ''}`}
                      aria-pressed={on}
                      disabled={busy}
                      onClick={() => {
                        setRace(r);
                      }}
                    >
                      <span class="wk-race-name">{copy?.name ?? ''}</span>
                    </button>
                  );
                })}
              </div>
            </section>

            <section class="wk-block" aria-labelledby="wk-cap-calling">
              <h2 class="wk-cap" id="wk-cap-calling">
                The Calling
              </h2>
              <div class="wk-callings" role="group" aria-labelledby="wk-cap-calling">
                {list.map((c) => {
                  const on = c.id === calling;
                  return (
                    <button
                      key={String(c.id)}
                      type="button"
                      class={`wk-calling${on ? ' on' : ''}`}
                      aria-pressed={on}
                      disabled={busy}
                      onClick={() => {
                        setCalling(c.id);
                      }}
                    >
                      <span class="wk-calling-mark" aria-hidden="true">
                        <Icon name={CALLING_ICON[c.id] ?? 'calling'} size={34} />
                      </span>
                      <span class="wk-calling-name">{c.name}</span>
                      <span class="wk-calling-tag">{c.tagline}</span>
                      <span class="wk-calling-abilities" aria-hidden="true">
                        {c.abilities.map((a) =>
                          a ? (
                            <i key={a.id}>
                              <Icon name={isIconName(a.id) ? a.id : 'pip'} size={16} />
                            </i>
                          ) : null,
                        )}
                      </span>
                    </button>
                  );
                })}
              </div>
            </section>
          </div>

          {/* The body itself, turning. The canvas keeps its box whether or not a device was
              had: a column that collapses when WebGPU is missing reflows the whole screen. */}
          <div class={`wk-stage${preview === 'live' ? ' live' : ''}`}>
            <canvas class="wk-canvas" ref={canvasRef} aria-hidden="true" />
            <p class="wk-stage-wait">
              {preview === 'loading'
                ? 'Finding a body…'
                : preview === 'off'
                  ? 'No turntable on this browser. The choice still stands.'
                  : race === 0
                    ? 'Choose a body.'
                    : ''}
            </p>
            {voice ? (
              <blockquote class="wk-voice">
                <p class="wk-voice-text">{voice.text}</p>
                <footer class="wk-voice-who">{voice.speaker}</footer>
              </blockquote>
            ) : null}
          </div>
        </div>
      </div>

      <footer class="wk-foot">
        <button
          type="button"
          class={`wk-wake${busy ? ' busy' : ''}`}
          // Not disabled while the write is out: `wake()` already refuses a second call, and the
          // one action this screen performs should not look like the screen broke.
          disabled={!ready}
          aria-busy={busy}
          aria-describedby="wk-gate"
          onClick={wake}
        >
          {busy ? 'Waking' : 'Wake'}
        </button>
        {/* The reason the sash is shut, said plainly and immediately under it — and pointed at
            by the button itself, so a screen reader hears it without hunting. */}
        <p class={`wk-gate${error ? ' bad' : ''}`} id="wk-gate" role="status">
          {error
            ? error
            : !named
              ? 'Give the ledger a name first.'
              : race === 0
                ? 'A body is chosen once and never again.'
                : calling === 0
                  ? 'Choose a Calling. That one can be changed later.'
                  : 'The Calling can be changed later. The body cannot.'}
        </p>
      </footer>
    </div>
  );
}
